import { useCallback, useMemo, useState, type ChangeEvent } from 'react';

import { useDebounce } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { Folder } from '@mint-lib/icons';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  FeaturedIcon,
  typographyVariants,
} from '@mint-lib/tw-components';
import { SearchBar } from '@mint-lib/tw-components/patterns';
import { useAtomValue } from 'jotai';

import { type MoveVerdict } from '../../../../helpers/fileSystemMove.js';
import { splitFileSystemNodes } from '../../../../helpers/fileSystemNode.js';
import { useFileSystemNodes } from '../../../../hooks/useFileSystemNodes.js';
import { useFolderNavigation } from '../../../../hooks/useFolderNavigation.js';
import { folderPathAtom } from '../../../../store/fileSystem.js';
import {
  MAX_FOLDER_DEPTH,
  ROOT_FOLDER_ID,
  type FileSystemNode,
  type FolderCrumb,
  type FolderNode,
} from '../../../../types/fileSystem.js';
import { FileSystemBreadcrumbs } from '../Breadcrumbs/Breadcrumbs.jsx';
import { NavigationFolderItem } from '../FolderSection/FolderItem.jsx';

const MoveNodesDialog = ({
  nodes,
  open,
  onClose,
  onMoveNodes,
  canMoveNodes,
}: {
  nodes: FileSystemNode[];
  open: boolean;
  onClose: () => void;
  onMoveNodes: (nodes: FileSystemNode[], targetFolderId: string) => void;
  canMoveNodes: (
    nodes: FileSystemNode[],
    targetFolderId: string,
  ) => MoveVerdict;
}) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 1000);

  const handleChangeSearch = useCallback(({ target }: ChangeEvent) => {
    setSearchValue((target as HTMLInputElement).value);
  }, []);

  const handleClearSearch = useCallback(() => setSearchValue(''), []);

  const path = useAtomValue(folderPathAtom);
  const [movePath, setMovePath] = useState<FolderCrumb[]>(path);

  const { depth, goToCrumb, enterFolder, currentFolderId } =
    useFolderNavigation({
      path: movePath,
      setPath: setMovePath,
    });

  const { folders } = useFileSystemNodes({
    folderId: currentFolderId,
    depth,
    search: debouncedSearchValue,
  });

  const otherFolders = useMemo(
    () => folders.filter((f) => !nodes.includes(f)),
    [folders, nodes],
  );

  const canContainSubfolder = useMemo(() => {
    return depth + 1 < MAX_FOLDER_DEPTH; // Full depth of tree should be checked on BE side
  }, [depth]);

  const breadcrumbItems = useMemo(
    () => [{ folderId: ROOT_FOLDER_ID, name: t('My Files') }, ...movePath],
    [movePath, t],
  );

  const handleMoveNode = () => {
    onMoveNodes(nodes, currentFolderId);
    setMovePath([]);
    onClose();
  };

  const onOpenFolder = useCallback(
    (folder: FolderNode) => {
      enterFolder(folder);
    },
    [enterFolder],
  );

  if (!nodes.length) {
    return null;
  }

  const {
    foldersCount,
    filesCount,
    files: movedFiles,
    folders: movedFolders,
  } = splitFileSystemNodes(nodes);

  const hasOnlyFiles = foldersCount === 0 && filesCount > 0;

  const getDialogLabel = () => {
    if (foldersCount > 0 && filesCount > 0) {
      return t('Move items');
    }

    if (foldersCount > 0) {
      if (foldersCount === 1) {
        return t('Move {{folderName}}', {
          folderName: [...movedFolders][0].name,
        });
      }

      return t('Move folders');
    }

    if (filesCount > 0) {
      if (filesCount === 1) {
        return t('Move {{fileName}}', {
          fileName: [...movedFiles][0].fileName,
        });
      }

      return t('Move files');
    }

    return '';
  };

  const isMoveButtonDisabled =
    !canMoveNodes(nodes, currentFolderId).allowed ||
    (!depth && !otherFolders.length); // Root level w/o other folders

  return (
    <Dialog modal onOpenChange={onClose} open={open}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot
          aria-describedby={undefined}
          size="s"
          className="max-h-[90vh]"
        >
          <DialogHeader>
            <DialogTitle>{getDialogLabel()}</DialogTitle>
          </DialogHeader>
          <DialogContent className="flex min-h-0 flex-col gap-4 overflow-y-auto">
            <SearchBar
              placeholder={t('Search a folder')}
              className="h-8 max-w-full"
              onChange={handleChangeSearch}
              onClear={handleClearSearch}
              value={searchValue}
            />
            <FileSystemBreadcrumbs
              items={breadcrumbItems}
              onFolderChange={goToCrumb}
              gridApi={null}
            />
            {otherFolders.length ? (
              otherFolders.map((folder) => (
                <NavigationFolderItem
                  key={folder.id}
                  folder={folder}
                  onOpen={
                    canContainSubfolder || hasOnlyFiles
                      ? onOpenFolder
                      : undefined
                  }
                />
              ))
            ) : (
              <div className="flex h-80 w-full flex-col items-center justify-center gap-4">
                <FeaturedIcon icon={<Folder />} />
                <p
                  className={typographyVariants({
                    variant: 'h4',
                  })}
                >
                  {t('This folder is empty')}
                </p>
              </div>
            )}
          </DialogContent>
          <DialogFooter>
            <Button size="large" variant="text" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button
              size="large"
              onClick={handleMoveNode}
              disabled={isMoveButtonDisabled}
            >
              {t('Move here')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

export { MoveNodesDialog };
